import { useMemo, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import FormDescriptionInput from "../../../../components/form_fields/FormDescriptionInput";
import Label from "../../../../components/form_fields/Label";
import MoneyInput from "../../../../components/form_fields/MoneyInput";
import ModalComponent from "../../../../components/modals/ModalFormComponent";
import APISelectField from "../../../../components/form_fields/APISelectField";
import FormCodeInput from "../../../../components/form_fields/FormCodeInput";
import MultiSelectField from "../../../../components/form_fields/MultiSelectField";
import RateComponent from "../../../../components/form_fields/RateComponent";
import { EmployeeDto } from "../../../../models/employee.dto";
import {
	PAY_PERIOD_STATUS,
	PayPeriodDto,
} from "../../../../models/pay-period.dto";
import {
	AdjustmentType,
	CreateSalaryAdjustmentDto,
	SalaryAdjustmentDto,
	parseNotificationRecipients,
} from "../../../../models/salary-adjustment.dto";
import {
	useCreateSalaryAdjustment,
	useUpdateSalaryAdjustment,
} from "../../../../services/salary-adjustment.service";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import { PATHS } from "../../../../routes/routes.paths";
import SalaryAdjustmentConfirmModal from "./SalaryAdjustmentConfirmModal";
import { hasPayPeriodStarted } from "../data";

enum AdjustmentValueMode {
	AMOUNT = "AMOUNT",
	PERCENT = "PERCENT",
}

interface IFormInputs {
	name: string;
	code: string;
	description: string;
	adjustmentType: AdjustmentType | "";
	adjustmentAmount?: number;
	/** Entered as a percentage (e.g. 10), sent to the server as a fraction (0.1) */
	adjustmentPercent?: number;
	payPeriodId?: number;
}

type SalaryAdjustmentModalFormProps = {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	title: string;
	isEditForm: boolean;
	adjustment: SalaryAdjustmentDto | null;
	payPeriods: PayPeriodDto[];
	employees: EmployeeDto[];
};

const inputClassName = (hasError: boolean) =>
	`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
		hasError
			? " border border-red-500 focus:border-red-500 focus:outline-red-500"
			: "border-none"
	}`;

const SalaryAdjustmentModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	adjustment,
	payPeriods,
	employees,
}: SalaryAdjustmentModalFormProps) => {
	const { currentCompany } = useCompanyStore();
	const editing = isEditForm && !!adjustment;
	const [uploading, setUploading] = useState(false);
	const [valueMode, setValueMode] = useState<AdjustmentValueMode>(
		editing && adjustment?.adjustmentAmount === null
			? AdjustmentValueMode.PERCENT
			: AdjustmentValueMode.AMOUNT,
	);
	const defaultRecipients = useMemo(
		() =>
			editing
				? parseNotificationRecipients(adjustment?.notificationRecipients || null)
				: [],
		// eslint-disable-next-line
		[],
	);
	const [recipientIds, setRecipientIds] = useState<number[]>(defaultRecipients);
	const [pendingPayload, setPendingPayload] =
		useState<CreateSalaryAdjustmentDto | null>(null);

	// The API cannot clear a pay period once set, so it stays required on edit
	const payPeriodRequired = editing && !!adjustment?.payPeriodId;

	const {
		register,
		watch,
		handleSubmit,
		setValue,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		// Only the active value field (amount or percent) is validated/submitted
		shouldUnregister: true,
		defaultValues: {
			name: editing ? adjustment?.name : "",
			code: editing ? adjustment?.code : "",
			description: editing ? adjustment?.description : "",
			adjustmentType: editing ? adjustment?.adjustmentType : "",
			adjustmentPercent:
				editing && adjustment?.adjustmentPercent !== null
					? Number(
							(Number(adjustment?.adjustmentPercent) * 100).toFixed(2),
						)
					: undefined,
			payPeriodId: editing ? adjustment?.payPeriodId || undefined : undefined,
		},
	});

	const adjustmentType = watch("adjustmentType");
	const { mutate: createSalaryAdjustment } = useCreateSalaryAdjustment();
	const { mutate: updateSalaryAdjustment } = useUpdateSalaryAdjustment();

	// Adjustments are applied when their pay period starts, so only open ones
	// that haven't started can be picked; the current selection is kept
	const payPeriodOptions = useMemo(
		() =>
			payPeriods.filter(
				(payPeriod) =>
					(payPeriod.status !== PAY_PERIOD_STATUS.CLOSED &&
						!hasPayPeriodStarted(payPeriod.startDate)) ||
					payPeriod.id === adjustment?.payPeriodId,
			),
		[payPeriods, adjustment?.payPeriodId],
	);

	const handleClose = () => {
		setOpen(false);
		reset();
	};

	const submitPayload = (payload: CreateSalaryAdjustmentDto) => {
		setUploading(true);

		if (editing && adjustment) {
			updateSalaryAdjustment(
				{
					companyId: currentCompany?.id as number,
					id: adjustment.id,
					payload,
				},
				{
					onSuccess: () => handleClose(),
					onSettled: () => setUploading(false),
				},
			);
			return;
		}

		createSalaryAdjustment(
			{ companyId: currentCompany?.id as number, payload },
			{
				onSuccess: () => {
					setPendingPayload(null);
					handleClose();
				},
				onSettled: () => setUploading(false),
			},
		);
	};

	const onSubmit: SubmitHandler<IFormInputs> = (data) => {
		const payPeriodId =
			data.payPeriodId && !Number.isNaN(data.payPeriodId)
				? data.payPeriodId
				: undefined;

		const payload: CreateSalaryAdjustmentDto = {
			name: data.name,
			code: data.code.trim().toUpperCase(),
			description: data.description || undefined,
			adjustmentType: data.adjustmentType as AdjustmentType,
			payPeriodId,
			notificationRecipients: recipientIds,
			...(valueMode === AdjustmentValueMode.AMOUNT
				? { adjustmentAmount: Number(data.adjustmentAmount) }
				: {
						adjustmentPercent: Number(
							(Number(data.adjustmentPercent) / 100).toFixed(4),
						),
					}),
		};

		// Without a pay period the adjustment is applied as soon as it is created
		if (!editing && !payPeriodId) {
			setPendingPayload(payload);
			return;
		}

		submitPayload(payload);
	};

	const validatePercent = (value: number) => {
		if (Number.isNaN(value)) return "Enter percentage here";
		if (value <= 0) return "Percentage must be greater than 0";
		if (value > 100) return "Percentage cannot be more than 100";
		// Stored as a fraction to 4 decimal places
		if (!/^\d+(\.\d{1,2})?$/.test(String(value))) {
			return "Use at most 2 decimal places";
		}
		if (adjustmentType === AdjustmentType.DECREASE && value >= 100) {
			return "A decrease must be less than 100%";
		}
		return true;
	};

	const validateAmount = (value: number) => {
		if (Number.isNaN(value) || !value) return "Enter amount here";
		if (value <= 0) return "Amount must be greater than 0";
		return true;
	};

	return (
		<ModalComponent open={open} title={title} handleClose={handleClose}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="grid grid-cols-2 gap-x-4 gap-y-6">
					<p className="col-span-2 text-sm text-amber-600 bg-amber-50 rounded-lg px-4 py-2">
						This adjustment changes the basic pay of every employee and every
						notch amount in the company.
					</p>
					<div className="w-full">
						<Label title="Name" for="name" />
						<input
							placeholder={"Enter name"}
							type="text"
							{...register("name", {
								required: "Enter name here",
							})}
							id="name"
							className={inputClassName(!!errors.name)}
						/>
						{errors.name && (
							<p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Unique Code" for="code" />
						<FormCodeInput
							register={register}
							attributes={{
								className: `uppercase ${inputClassName(!!errors.code)}`,
							}}
						/>
						{errors.code && (
							<p className="text-red-500 text-sm mt-1">{errors.code.message}</p>
						)}
					</div>
					<div className="w-full col-span-2">
						<Label title="Description" for="description" optional />
						<FormDescriptionInput
							register={register}
							required={false}
							attributes={{
								className: inputClassName(!!errors.description),
							}}
						/>
					</div>
					<div className="w-full">
						<Label title="Adjustment Type" for="adjustmentType" />
						<select
							id="adjustmentType"
							{...register("adjustmentType", {
								required: "Select adjustment type here",
							})}
							className={inputClassName(!!errors.adjustmentType)}
						>
							<option value="">Select Adjustment Type</option>
							<option value={AdjustmentType.INCREASE}>Increase</option>
							<option value={AdjustmentType.DECREASE}>Decrease</option>
						</select>
						{errors.adjustmentType && (
							<p className="text-red-500 text-sm mt-1">
								{errors.adjustmentType.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Adjust By" for="valueMode" />
						<div
							id="valueMode"
							className="w-full bg-secondary-gray rounded-lg h-14 mt-2 p-1 flex text-sm"
						>
							{[
								{ mode: AdjustmentValueMode.AMOUNT, label: "Fixed Amount" },
								{ mode: AdjustmentValueMode.PERCENT, label: "Percentage" },
							].map(({ mode, label }) => (
								<button
									key={mode}
									type="button"
									onClick={() => setValueMode(mode)}
									className={`flex-1 rounded-md transition-colors ${
										valueMode === mode
											? "bg-white shadow-sm font-medium text-primary-mango"
											: "text-gray-500 hover:text-gray-700"
									}`}
								>
									{label}
								</button>
							))}
						</div>
					</div>
					{valueMode === AdjustmentValueMode.AMOUNT ? (
						<div className="w-full">
							<Label title="Amount" for="adjustmentAmount" />
							<MoneyInput
								attributes={{
									id: "adjustmentAmount",
									placeholder: "0",
								}}
								register={register}
								name="adjustmentAmount"
								setValue={setValue}
								defaultValue={
									editing && Number(adjustment?.adjustmentAmount) > 0
										? (adjustment?.adjustmentAmount as string)
										: undefined
								}
								required={"Enter amount here"}
								validate={validateAmount}
								error={errors.adjustmentAmount}
							/>
							{errors.adjustmentAmount && (
								<p className="text-red-500 text-sm mt-1">
									{errors.adjustmentAmount.message}
								</p>
							)}
						</div>
					) : (
						<div className="w-full">
							<Label title="Percentage" for="adjustmentPercent" />
							<RateComponent
								attributes={{
									id: "adjustmentPercent",
									min: 0,
									max: 100,
								}}
								register={register}
								name="adjustmentPercent"
								required="Enter percentage here"
								validate={validatePercent}
								error={errors.adjustmentPercent}
							/>
							{errors.adjustmentPercent && (
								<p className="text-red-500 text-sm mt-1">
									{errors.adjustmentPercent.message}
								</p>
							)}
						</div>
					)}
					<div className="w-full">
						<Label
							title="Pay Period"
							for="payPeriodId"
							optional={!payPeriodRequired}
						/>
						<APISelectField
							key="payPeriodId"
							attributes={{
								id: "payPeriodId",
							}}
							placeholder={
								payPeriodRequired ? "Select Pay Period" : "Apply immediately"
							}
							register={register}
							name="payPeriodId"
							options={payPeriodOptions}
							mapText="code"
							pathname={PATHS.SUB_COMPANY_SETTINGS.PAY_PERIODS}
							required={payPeriodRequired ? "Select pay period here" : false}
							error={errors.payPeriodId}
							valueAsNumber
						/>
						{errors.payPeriodId ? (
							<p className="text-red-500 text-sm mt-1">
								{errors.payPeriodId.message}
							</p>
						) : (
							<p className="text-xs text-gray-500 mt-1">
								{payPeriodRequired
									? "Applied automatically shortly after the start date of the selected pay period begins. Only pay periods that haven't started can be selected."
									: "Leave empty to apply immediately, so payroll runs from now on use the new salaries, including the current pay period's if not yet run. Or pick a pay period that hasn't started to have it applied automatically shortly after its start date begins."}
							</p>
						)}
					</div>
					<div className="w-full col-span-2">
						<Label
							title="Notification Recipients"
							for="notificationRecipients"
							optional
						/>
						<MultiSelectField
							key="notificationRecipients"
							options={employees}
							setResourceIds={setRecipientIds}
							defaultValues={defaultRecipients}
							placeholder={"Select Employees"}
							customMapText={(employee: EmployeeDto) =>
								`${employee.firstName} ${employee.lastName}`
							}
							pathname={PATHS.EMPLOYEES}
							required={false}
						/>
					</div>
				</div>

				<div className="w-full flex justify-end mt-8">
					<OrangeButton
						type="submit"
						title={
							uploading
								? "Uploading..."
								: editing
									? "Update Salary Adjustment"
									: "Add Salary Adjustment"
						}
						className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
						disabled={uploading}
					/>
				</div>
			</form>

			<SalaryAdjustmentConfirmModal
				open={!!pendingPayload}
				title="Apply salary adjustment immediately?"
				confirmText="Yes, Apply Now"
				confirmingText="Applying..."
				confirming={uploading}
				onConfirm={() => pendingPayload && submitPayload(pendingPayload)}
				onCancel={() => setPendingPayload(null)}
			>
				<p>
					No pay period was selected, so this adjustment will be applied{" "}
					<span className="font-bold">immediately</span> after it is created.
				</p>
				<p>
					It will{" "}
					<span className="font-bold">
						{pendingPayload?.adjustmentType === AdjustmentType.DECREASE
							? "decrease"
							: "increase"}
					</span>{" "}
					the basic pay of <span className="font-bold">every employee</span>{" "}
					and <span className="font-bold">every notch amount</span> in the
					company.
				</p>
			</SalaryAdjustmentConfirmModal>
		</ModalComponent>
	);
};

export default SalaryAdjustmentModalForm;
